import { useEffect, useRef, useState, type DragEvent, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

// ===========================================================================
// CameraTile — una celda del mosaico de video (estilo «Vista principal» de
// iVMS-4200). Autocontenida: no depende de las dashcams ni de Monitoreo IA, así
// que viaja igual a los otros tenants del monorepo.
//
//   • Sin stream: pinta la MINIATURA real de la cámara con su botón de play; al
//     pulsarlo avisa «Sin señal de video en este momento».
//   • Con stream (`streamUrl` que el navegador pueda abrir: MP4/HLS nativo/
//     MJPEG vía <video>), lo reproduce; si falla, vuelve a la miniatura.
//   • OSD dibujado en la interfaz (nunca en la imagen): nombre, fecha y hora en
//     vivo y el indicador REC / EN LÍNEA / SIN CONEXIÓN.
//   • Controles: play/pausa, captura (PNG con el OSD), silencio y pantalla
//     completa. Doble clic → maximizar (lo decide quien la contiene).
// ===========================================================================

export type CameraTileStatus = "online" | "recording" | "offline" | "maintenance";

export interface CameraTileSource {
  id: string;
  name: string;
  siteName?: string;
  status: CameraTileStatus;
  thumbnailUrl?: string | null;
  snapshotUrl?: string | null;
  streamUrl?: string | null;
  audio?: boolean;
  lastSeenAt?: string | null;
}

export const CAMERA_DRAG_MIME = "application/x-camera-id";

export interface CameraTileProps {
  camera: CameraTileSource | null;
  /** Usa la foto grande (1 o 4 celdas, o celda maximizada). */
  hd?: boolean;
  selected?: boolean;
  maximized?: boolean;
  /** Número de canal/celda que se muestra en la esquina. */
  index?: number;
  compact?: boolean;
  onSelect?: () => void;
  onToggleMaximize?: () => void;
  onDropCamera?: (cameraId: string) => void;
  onClear?: () => void;
  className?: string;
}

/**
 * Reloj compartido por todas las celdas: un solo intervalo por pestaña. Las
 * celdas compactas (8 chicas / 12) no muestran la hora y no se suscriben.
 */
const clockListeners = new Set<(ms: number) => void>();
let clockTimer: ReturnType<typeof setInterval> | null = null;
function useNow(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    clockListeners.add(setNow);
    if (!clockTimer) clockTimer = setInterval(() => clockListeners.forEach((l) => l(Date.now())), 1000);
    return () => {
      clockListeners.delete(setNow);
      if (!clockListeners.size && clockTimer) {
        clearInterval(clockTimer);
        clockTimer = null;
      }
    };
  }, [enabled]);
  return now;
}

/** Celdas suscritas al reloj (para las pruebas). */
export const clockSubscribers = () => clockListeners.size;

export function formatOsdTime(ms: number, locale: string): string {
  const d = new Date(ms);
  const date = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short" }).format(d);
  const time = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(d);
  return `${date} ${weekday} ${time}`;
}

export default function CameraTile({
  camera,
  hd = false,
  selected = false,
  maximized = false,
  index,
  compact = false,
  onSelect,
  onToggleMaximize,
  onDropCamera,
  onClear,
  className = "",
}: CameraTileProps) {
  const { t, i18n } = useTranslation("accessConsole");
  const now = useNow(Boolean(camera) && !compact);
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [streamFailed, setStreamFailed] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [flash, setFlash] = useState(false);

  const cameraId = camera?.id;
  useEffect(() => {
    setPlaying(false);
    setStreamFailed(false);
  }, [cameraId]);

  const hasStream = Boolean(camera?.streamUrl) && !streamFailed;
  const offline = camera?.status === "offline";
  const image = camera ? (hd ? camera.snapshotUrl || camera.thumbnailUrl : camera.thumbnailUrl || camera.snapshotUrl) : null;
  const showNoSignal = playing && !hasStream;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing && hasStream) void v.play().catch(() => setStreamFailed(true));
    else v.pause();
  }, [playing, hasStream]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  const stop = (e: MouseEvent) => e.stopPropagation();

  const togglePlay = (e: MouseEvent) => {
    stop(e);
    setPlaying((p) => !p);
  };

  const fullscreen = (e: MouseEvent) => {
    stop(e);
    const el = rootRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void el.requestFullscreen?.().catch(() => undefined);
  };

  /** Captura: el cuadro actual (o la miniatura) con el OSD, en PNG. */
  const capture = async (e: MouseEvent) => {
    stop(e);
    if (!camera) return;
    const canvas = document.createElement("canvas");
    const v = videoRef.current;
    const draw = (src: CanvasImageSource, w: number, h: number) => {
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return false;
      ctx.drawImage(src, 0, 0, w, h);
      const pad = Math.round(h * 0.03);
      ctx.font = `600 ${Math.max(14, Math.round(h * 0.035))}px ui-monospace, monospace`;
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.shadowColor = "rgba(0,0,0,0.85)";
      ctx.shadowBlur = 4;
      ctx.textBaseline = "top";
      ctx.fillText(formatOsdTime(Date.now(), i18n.language), pad, pad);
      ctx.textBaseline = "bottom";
      ctx.fillText(camera.name, pad, h - pad);
      return true;
    };
    let ok = false;
    if (v && playing && hasStream && v.videoWidth) ok = draw(v, v.videoWidth, v.videoHeight);
    else if (image) {
      const img = new Image();
      img.src = camera.snapshotUrl || image;
      await img.decode().catch(() => undefined);
      if (img.naturalWidth) ok = draw(img, img.naturalWidth, img.naturalHeight);
    }
    if (!ok) return;
    try {
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `${camera.name.replace(/[^\p{L}\p{N}]+/gu, "_")}_${stamp}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
      setFlash(true);
      setTimeout(() => setFlash(false), 180);
    } catch {
      /* lienzo contaminado (imagen de otro origen): no se puede exportar */
    }
  };

  const onDragOver = (e: DragEvent) => {
    if (!onDropCamera) return;
    if (Array.from(e.dataTransfer.types).includes(CAMERA_DRAG_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setDragOver(true);
    }
  };
  const onDrop = (e: DragEvent) => {
    setDragOver(false);
    const id = e.dataTransfer.getData(CAMERA_DRAG_MIME);
    if (id && onDropCamera) {
      e.preventDefault();
      onDropCamera(id);
    }
  };

  const ring = dragOver ? "ring-2 ring-info" : selected ? "ring-2 ring-primary-500" : "ring-1 ring-black/40";

  return (
    <div
      ref={rootRef}
      role="button"
      tabIndex={0}
      aria-label={camera ? camera.name : t("video.emptySlot")}
      aria-pressed={selected}
      onClick={onSelect}
      onDoubleClick={onToggleMaximize}
      onKeyDown={(e) => {
        if (e.key === "Enter") onToggleMaximize?.();
      }}
      onDragOver={onDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`group relative h-full w-full min-h-0 min-w-0 select-none overflow-hidden bg-black outline-none ${ring} ${className}`}
      data-testid="camera-tile"
    >
      {!camera ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-white/30">
          <i className={`ri-add-line ${compact ? "text-xl" : "text-3xl"}`} />
          {!compact && <span className="px-2 text-center text-[11px]">{t("video.emptySlot")}</span>}
          {index !== undefined && <span className="absolute left-1.5 top-1 font-mono text-[10px] text-white/30">{index + 1}</span>}
        </div>
      ) : (
        <>
          {image && (
            <img
              src={image}
              alt={camera.name}
              draggable={false}
              loading="lazy"
              className={`absolute inset-0 h-full w-full object-cover ${offline ? "opacity-40 grayscale" : ""} ${playing && hasStream ? "invisible" : ""}`}
            />
          )}
          {hasStream && (
            <video
              ref={videoRef}
              src={camera.streamUrl ?? undefined}
              className={`absolute inset-0 h-full w-full object-cover ${playing ? "" : "hidden"}`}
              playsInline
              muted={muted}
              onError={() => setStreamFailed(true)}
            />
          )}

          {/* OSD superior: canal + nombre y estado */}
          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-2 bg-gradient-to-b from-black/70 to-transparent px-2 pb-4 pt-1.5">
            <span className={`min-w-0 truncate font-semibold text-white drop-shadow ${compact ? "text-[10px]" : "text-xs"}`}>
              {index !== undefined && <span className="mr-1 font-mono text-white/60">{String(index + 1).padStart(2, "0")}</span>}
              {camera.name}
            </span>
            <span
              className={`flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                offline ? "bg-white/15 text-white/70" : camera.status === "maintenance" ? "bg-warning/90 text-white" : camera.status === "recording" ? "bg-error/90 text-white" : "bg-success/90 text-white"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${offline ? "bg-white/50" : "animate-pulse bg-white"}`} />
              {t(`video.state.${camera.status}`)}
            </span>
          </div>

          {/* OSD inferior: fecha y hora en vivo */}
          {!compact && (
            <div className="pointer-events-none absolute bottom-1.5 left-2 font-mono text-[11px] text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.9)]">
              {formatOsdTime(now, i18n.language)}
            </div>
          )}

          {/* Estado central */}
          {offline ? (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 text-center text-white/80">
              <i className={`ri-wifi-off-line ${compact ? "text-xl" : "text-3xl"}`} />
              <span className="text-xs font-semibold">{t("video.offline")}</span>
            </div>
          ) : (
            !(playing && hasStream) && (
              <button
                type="button"
                onClick={togglePlay}
                aria-label={t("video.play")}
                className={`absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition hover:bg-black/65 ${
                  compact ? "h-8 w-8 text-lg" : "h-12 w-12 text-2xl"
                } ${playing ? "opacity-0" : "opacity-80 group-hover:opacity-100"}`}
              >
                <i className="ri-play-fill" />
              </button>
            )
          )}
          {showNoSignal && !offline && (
            <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-center px-3">
              <span className="flex items-center gap-1.5 rounded-md bg-black/70 px-2.5 py-1.5 text-[11px] font-medium text-white">
                <i className="ri-signal-wifi-error-line text-warning" />
                {t("video.noSignal")}
              </span>
            </div>
          )}

          {/* Barra de controles */}
          <div
            className={`absolute bottom-0 right-0 flex items-center gap-0.5 rounded-tl-md bg-black/60 px-1 py-0.5 text-white transition-opacity [@media(pointer:coarse)]:opacity-100 ${
              selected || maximized ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
            }`}
            onDoubleClick={stop}
          >
            <TileButton icon={playing ? "ri-pause-fill" : "ri-play-fill"} label={t(playing ? "video.pause" : "video.play")} onClick={togglePlay} disabled={offline} />
            <TileButton icon="ri-camera-line" label={t("video.capture")} onClick={capture} />
            <TileButton icon={muted ? "ri-volume-mute-line" : "ri-volume-up-line"} label={t(muted ? "video.unmute" : "video.mute")} onClick={(e) => { stop(e); setMuted((m) => !m); }} disabled={!camera.audio} />
            <TileButton icon={maximized ? "ri-collapse-diagonal-line" : "ri-expand-diagonal-line"} label={t(maximized ? "video.restore" : "video.maximize")} onClick={(e) => { stop(e); onToggleMaximize?.(); }} />
            <TileButton icon="ri-fullscreen-line" label={t("video.fullscreen")} onClick={fullscreen} />
            {onClear && <TileButton icon="ri-close-line" label={t("video.clear")} onClick={(e) => { stop(e); onClear(); }} />}
          </div>
          {flash && <div className="pointer-events-none absolute inset-0 bg-white/60" />}
        </>
      )}
    </div>
  );
}

function TileButton({ icon, label, onClick, disabled }: { icon: string; label: string; onClick: (e: MouseEvent) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="flex h-6 w-6 items-center justify-center rounded text-sm hover:bg-white/20 disabled:opacity-35 [@media(pointer:coarse)]:h-9 [@media(pointer:coarse)]:w-9"
    >
      <i className={icon} />
    </button>
  );
}
